import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ClearFieldButton from './ClearFieldButton';

const parseFormerNames = (raw: string): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(trimmed);
  }
  return names;
};

interface EditableFormerNamesCellProps {
  companyId: string;
  value?: string[];
  canEdit: boolean;
  onSave: (companyId: string, value: string[]) => Promise<void>;
}

const EditableFormerNamesCell: React.FC<EditableFormerNamesCellProps> = ({
  companyId,
  value,
  canEdit,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const names = value || [];
  const hasValue = names.length > 0;
  const display = hasValue ? names.join(', ') : '—';

  const startEditing = () => {
    setDraft(names.join(', '));
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, parseFormerNames(draft));
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, []);
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box sx={{ mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
          <strong>Former names:</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Comma-separated former names"
            size="small"
            autoFocus
            multiline
            minRows={2}
            error={Boolean(error)}
            helperText={error || 'Separate multiple names with commas'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
              if (e.key === 'Escape') cancelEditing();
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {saving ? (
            <CircularProgress size={18} sx={{ mt: 1, mx: 0.5 }} />
          ) : (
            <Box sx={{ display: 'flex', mt: 0.5 }}>
              <Tooltip title="Save">
                <IconButton size="small" color="success" onClick={handleSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelEditing}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Former names:</strong> {display}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        <strong>Former names:</strong> {display}
      </Typography>
      {hasValue ? (
        <>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={startEditing} disabled={saving}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <ClearFieldButton onClear={handleClear} disabled={saving} />
        </>
      ) : (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={startEditing}
          sx={{ fontSize: '0.8125rem', flexShrink: 0 }}
        >
          + Add names
        </Link>
      )}
    </Box>
  );
};

export default EditableFormerNamesCell;
